import { useEffect, useRef, useState, type ReactNode } from "react";
import { Modal, Stack, Group, Text, Button, Badge, Anchor } from "@mantine/core";
import { IconAlertTriangle, IconMapPin, IconPhone } from "@tabler/icons-react";
import Pusher from "pusher-js";
import { useNavigate } from "react-router-dom";
import { PUSHER_KEY, PUSHER_CLUSTER } from "../../config/env";
import { humanize } from "../../lib/labels";
import { formatDateTime } from "../../lib/format";

interface SosAlert {
  _id: string;
  tripID?: string;
  name?: string;
  phoneNumber?: string;
  occurredBy?: string;
  occurredAt?: string;
  location?: { latitude?: number; longitude?: number };
}

interface AlarmRef {
  ctx?: AudioContext;
  timer?: number;
}

function startAlarm(ref: React.MutableRefObject<AlarmRef>) {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!ref.current.ctx) ref.current.ctx = new Ctx();
    const ctx = ref.current.ctx;
    void ctx.resume?.();
    const beep = () => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      o.frequency.value = 880;
      g.gain.value = 0.12;
      o.connect(g);
      g.connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.25);
    };
    beep();
    ref.current.timer = window.setInterval(beep, 900);
  } catch {
    // Audio blocked (no user gesture yet) — the visual modal still alerts.
  }
}

function stopAlarm(ref: React.MutableRefObject<AlarmRef>) {
  if (ref.current.timer) {
    clearInterval(ref.current.timer);
    ref.current.timer = undefined;
  }
}

// Mount inside the authenticated shell. Subscribes to the backend's SOS admin
// channel and raises a blocking, ringing modal for each new alert until an
// admin acknowledges it.
export function SosAlertProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<SosAlert[]>([]);
  const alarm = useRef<AlarmRef>({});
  const current = alerts[0];

  useEffect(() => {
    if (!PUSHER_KEY) return;
    const pusher = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
    const channel = pusher.subscribe("sos-admin-channel");
    channel.bind("new-alert", (data: SosAlert) => {
      if (!data?._id) return;
      setAlerts((prev) =>
        prev.some((a) => a._id === data._id) ? prev : [...prev, data],
      );
    });
    return () => {
      pusher.unsubscribe("sos-admin-channel");
      pusher.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!current) {
      stopAlarm(alarm);
      return;
    }
    startAlarm(alarm);
    return () => stopAlarm(alarm);
  }, [current?._id]);

  const acknowledge = () => setAlerts((prev) => prev.slice(1));
  const mapHref =
    current?.location?.latitude != null
      ? `https://www.google.com/maps?q=${current.location.latitude},${current.location.longitude}`
      : null;

  return (
    <>
      {children}
      <Modal
        opened={!!current}
        onClose={() => {}}
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
        centered
        title={
          <Group gap="xs">
            <IconAlertTriangle color="var(--mantine-color-red-6)" />
            <Text fw={800} c="red">
              SOS Alert
            </Text>
            {alerts.length > 1 && (
              <Badge color="red" variant="light">
                +{alerts.length - 1} more
              </Badge>
            )}
          </Group>
        }
      >
        {current && (
          <Stack gap="sm">
            <Group gap="xs">
              <Badge color={current.occurredBy === "DRIVER" ? "steel" : "oxblood"}>
                {humanize(current.occurredBy)}
              </Badge>
              <Text fw={600}>{current.name ?? "Unknown"}</Text>
            </Group>
            <Text size="sm">Raised: {formatDateTime(current.occurredAt)}</Text>
            {current.phoneNumber && (
              <Anchor href={`tel:${current.phoneNumber}`} size="sm">
                <Group gap={4}>
                  <IconPhone size={14} /> {current.phoneNumber}
                </Group>
              </Anchor>
            )}
            {mapHref && (
              <Anchor href={mapHref} target="_blank" rel="noopener noreferrer" size="sm">
                <Group gap={4}>
                  <IconMapPin size={14} /> View location on map
                </Group>
              </Anchor>
            )}
            <Group justify="flex-end" mt="sm">
              {current.tripID && (
                <Button
                  variant="light"
                  onClick={() => {
                    const id = current.tripID!;
                    acknowledge();
                    navigate(`/trips/${id}`);
                  }}
                >
                  View trip
                </Button>
              )}
              <Button color="red" onClick={acknowledge}>
                Acknowledge
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
